import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { inject, observer } from 'mobx-react';
import { isEqual, merge } from 'lodash';
import type Highcharts from 'highcharts';
import { Box, Button, Flex, Heading } from 'core/components';
import { WrappedBoxProps } from 'core/components/types';
import getErrorBoundary from 'core/util/getErrorBoundary';
import UdeDataView from 'app/views/ude/UdeDataView';
import QueryModel, { QuerySettings } from 'app/stores/ude/QueryModel';
import { Query } from '@kentik/ui-shared/query/query.types';
import $ude from 'app/stores/ude/$ude';
import { useHistory } from 'react-router-dom';
import { FiExternalLink } from 'react-icons/fi';

const ErrorBoundary = getErrorBoundary('UdeDataViewWrapper');

const DEFAULT_SETTINGS: QuerySettings = {
  chart: {
    enable: true,
    type: 'line',
    colors: 'default',
    legend: {
      enable: true,
      position: 'right',
      mode: 'condensed',
      showRollups: true,
      showEventsView: true
    },
    xAxis: { timezone: 'utc' },
    yAxis: { type: 'linear' }
  }
};

export interface UdeDataViewWrapperProps extends WrappedBoxProps {
  /** The UDE query to execute. Changes trigger re-execution. */
  query: Query;
  /** Partial settings deep-merged with defaults. */
  settings?: Partial<QuerySettings>;
  /** Padding applied to the chart. Defaults to 1 (theme unit). */
  padding?: number;
  /** Show "Open in Universal Data Explorer" button. Gated by `ude` permission. Default false. */
  showOpenInUde?: boolean;
  /** Optional callback to receive the Highcharts chart instance (for cross-chart sync). */
  onChartRef?: (chart: Highcharts.Chart | null) => void;
  /** When set, overrides the default max-width for a right-positioned legend. */
  legendMaxWidth?: number | string;
  /** Arbitrary Highcharts options deep-merged into the generated chart config. */
  chartOverrides?: Highcharts.Options;
  /** Optional title displayed above the chart as a Heading. */
  title?: string;
  /** Optional declarative signal to rerun the current query without changing query inputs. */
  refreshToken?: number;
  /** Replaces the default "No results found." placeholder (errors still use the default). */
  emptyState?: React.ReactNode;
  // Injected
  $auth?: { hasPermission: (path: string, options?: { overrideForSudo?: boolean }) => boolean };
}

function UdeDataViewWrapperBase({
  query,
  settings: settingsOverride,
  padding = 1,
  showOpenInUde = false,
  onChartRef,
  legendMaxWidth,
  chartOverrides,
  title,
  refreshToken,
  emptyState,
  $auth,
  ...boxProps
}: UdeDataViewWrapperProps) {
  const history = useHistory();
  const hasMounted = useRef(false);

  // Gate on embedded_ude permission
  const hasEmbeddedUdePermission = $auth?.hasPermission('embedded_ude', { overrideForSudo: false });
  const canOpenInUde = showOpenInUde && $auth?.hasPermission('ude', { overrideForSudo: false });

  // Deep-merge caller's partial settings with defaults
  const settings = useMemo<QuerySettings>(
    () => (settingsOverride ? merge({}, DEFAULT_SETTINGS, settingsOverride) : DEFAULT_SETTINGS),
    [settingsOverride]
  );

  // Create QueryModel once per component lifetime
  const [queryModel] = useState(() => new QueryModel(query, settings));

  // Sync query when inputs change
  useEffect(() => {
    if (!isEqual(queryModel.query, query)) {
      queryModel.query = query;
      queryModel.dirty = true;
    }
  }, [queryModel, query]);

  // Sync settings when inputs change
  useEffect(() => {
    if (!isEqual(queryModel.settings, settings)) {
      queryModel.settings = settings;
      queryModel.dirty = true;
    }
  }, [queryModel, settings]);

  // Rerun the current query when the caller explicitly requests a refresh.
  useEffect(() => {
    if (hasMounted.current) {
      queryModel.run();
      return;
    }

    hasMounted.current = true;
  }, [queryModel, refreshToken]);

  // Cleanup on unmount
  useEffect(
    () => () => {
      queryModel.cleanup();
    },
    [queryModel]
  );

  const handleOpenInUde = useCallback(() => {
    $ude.setPendingQuery({ query: queryModel.query, chart: queryModel.settings.chart });
    history.push('/v4/ude');
  }, [queryModel, history]);

  if (!hasEmbeddedUdePermission) {
    return null;
  }

  return (
    <>
      {(title || canOpenInUde) && (
        <Flex alignItems="center" justifyContent="space-between" gap={2} minHeight="24px">
          {title ? (
            <Heading level={5} m={0}>
              {title}
            </Heading>
          ) : (
            <Box />
          )}
          {canOpenInUde && (
            <Button minimal small icon={<FiExternalLink size={14} />} onClick={handleOpenInUde}>
              Open in Universal Data Explorer
            </Button>
          )}
        </Flex>
      )}
      <Box className="ude-dataview-wrapper" height="100%" mt={1} position="relative" {...boxProps}>
        <ErrorBoundary>
          <UdeDataView
            queryModel={queryModel}
            padding={padding}
            onChartRef={onChartRef}
            legendMaxWidth={legendMaxWidth}
            chartOverrides={chartOverrides}
            emptyState={emptyState}
          />
        </ErrorBoundary>
      </Box>
    </>
  );
}

const UdeDataViewWrapper = inject('$auth')(observer(UdeDataViewWrapperBase));
export default UdeDataViewWrapper;
export { DEFAULT_SETTINGS };
